import { useEffect, useCallback, useRef } from 'react';
import { GAME_CONFIG } from '../config/gameConfig';
import { RHYTHM_CONFIG } from '../config/RhythmConfig';
import { Timestamp } from 'firebase/firestore';
import { toKstIso } from '../utils/firestoreApi';

export const useRhythmRenderer = ({
  showResult,
  canvasRef,
  animationRef,
  gameAreaRef,
  canvasWidth,
  canvasHeight,
  currentBeatmap,
  audioController,
  NOTE_TRAVEL_TIME,
  PERFECT_WINDOW,
  GOOD_WINDOW,
  MISS_WINDOW,
  activeLanes,
  hitNotesRef,
  missedNotesRef,
  groupedNotesRef,
  onGameEnd,
  scoreRef,
  maxComboRef,
  perfectGroupsRef,
  groupCount,
  maxScoreForRound,
  setShowResult,
  currentTimeRef,
  comboRef,
  evaluateGroup,
  rhythmConfig = RHYTHM_CONFIG
}) => {
  // 부드러운 애니메이션을 위한 ref들
  const lastFrameTimeRef = useRef(0);
  const smoothTimeRef = useRef(0);
  const frameCountRef = useRef(0);

  // 풍선 터지는 효과를 위한 애니메이션 상태 관리
  const popAnimationsRef = useRef(new Map()); // noteId -> animation state

  // Safari 및 모바일 브라우저 대응
  useEffect(() => {
    const updateViewportHeight = () => {
      const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const vh = height * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    updateViewportHeight();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight);
    }
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
      }
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);

  // 풍선 터지는 효과 시작 함수
  const startPopAnimation = useCallback((noteId, note) => {
    // 노트의 현재 위치 계산
    const canvasW = canvasWidth;
    const canvasH = canvasHeight;
    const laneWidth = canvasW / 4;
    
    // 화면 크기에 따른 판정선 위치 조정
    let hitZoneY;
    if (window.innerWidth <= 768) {
      const screenHeight = window.innerHeight;
      if (screenHeight < 600) {
        hitZoneY = canvasH * (1 / 2);
      } else {
        hitZoneY = canvasH * (3 / 5);
      }
    } else {
      hitZoneY = canvasH * (3 / 5);
    }
    
    // 노트의 현재 위치 계산
    const appearTime = note.time - NOTE_TRAVEL_TIME;
    const currentTime = currentTimeRef.current() * 1000;
    
    let noteTop;
    if (currentTime < appearTime) {
      noteTop = -50;
    } else if (currentTime <= note.time) {
      const progress = Math.max(0, Math.min(1, (currentTime - appearTime) / NOTE_TRAVEL_TIME));
      noteTop = progress * hitZoneY;
    } else {
      const overTime = currentTime - note.time;
      const pxPerMs = hitZoneY / NOTE_TRAVEL_TIME;
      noteTop = hitZoneY + overTime * pxPerMs;
    }
    
    const x = laneWidth * note.lane + laneWidth * 0.1;
    const width = laneWidth * 0.8;
    const height = 20 * (canvasH / canvasHeight);
    const y = noteTop;
    
    const animationState = {
      startTime: performance.now(),
      duration: 300, // 300ms
      x,
      y,
      width,
      height,
      particles: []
    };

         // 노트를 여러 조각으로 분해
     const particleCount = 6; // 노트 조각 개수
           const colors = [
        '#FF0080', // 형광 핑크
        '#00FFFF', // 형광 시안
        '#00FF00', // 형광 그린
        '#FFFF00', // 형광 옐로우
        '#FF00FF', // 형광 마젠타
        '#0080FF', // 형광 블루
        '#FF8000', // 형광 오렌지
        '#8000FF', // 형광 퍼플
        '#00FF80', // 형광 민트
        '#FF4080'  // 형광 로즈
      ];
     
     for (let i = 0; i < particleCount; i++) {
       const angle = (Math.PI * 2 * i) / particleCount;
       const speed = 1 + Math.random() * 2;
       const particleSize = (width * height) / (particleCount * 4); // 노트 크기를 조각들로 나눔
       
       // 랜덤하게 색상 선택
       const colorIndex = Math.floor(Math.random() * colors.length);
       const baseColor = colors[colorIndex];
       
       animationState.particles.push({
         x: 0,
         y: 0,
         vx: Math.cos(angle) * speed,
         vy: Math.sin(angle) * speed,
         size: Math.sqrt(particleSize), // 면적을 기반으로 크기 계산
         alpha: 1,
         color: baseColor,
         originalColor: baseColor
       });
     }

    popAnimationsRef.current.set(noteId, animationState);
  }, [canvasWidth, canvasHeight, NOTE_TRAVEL_TIME, currentTimeRef]);

  // 풍선 터지는 효과 렌더링
  const renderPopAnimation = useCallback((ctx, animationState, currentTime) => {
    const elapsed = currentTime - animationState.startTime;
    const progress = Math.min(elapsed / animationState.duration, 1);
    
    // 이징 함수 (부드러운 시작과 끝)
    const easeOut = 1 - Math.pow(1 - progress, 3);
    
    // 노트 조각들 렌더링
    animationState.particles.forEach(particle => {
      const particleProgress = Math.min(elapsed / (animationState.duration * 0.8), 1);
      const particleEaseOut = 1 - Math.pow(1 - particleProgress, 2);
      
      // 조각 위치 계산 - 노트 중심에서 바깥으로 퍼짐
      particle.x = particle.vx * particleEaseOut * 30; // 더 멀리 퍼지도록
      particle.y = particle.vy * particleEaseOut * 30 + (particleProgress * 15); // 중력 효과
      
      // 조각 투명도 계산
      particle.alpha = Math.max(0, 1 - particleProgress);
      
      // 조각 크기 계산 - 점점 작아짐
      const sizeMultiplier = Math.max(0.1, 1 - particleProgress * 0.9);
      
             ctx.save();
       ctx.globalAlpha = particle.alpha;
       
       // 그라데이션 효과 추가
       const gradient = ctx.createRadialGradient(
         animationState.x + animationState.width / 2 + particle.x,
         animationState.y + animationState.height / 2 + particle.y,
         0,
         animationState.x + animationState.width / 2 + particle.x,
         animationState.y + animationState.height / 2 + particle.y,
         particle.size * sizeMultiplier
       );
       
       // 원본 색상에서 밝은 색상으로 그라데이션
       gradient.addColorStop(0, particle.color);
       gradient.addColorStop(0.7, particle.color);
       gradient.addColorStop(1, '#FFFFFF');
       
       ctx.fillStyle = gradient;
       ctx.shadowColor = particle.color;
       ctx.shadowBlur = 8;
       ctx.shadowOffsetX = 0;
       ctx.shadowOffsetY = 0;
       
       // 조각 그리기 (둥근 모서리 사각형)
       const pieceSize = particle.size * sizeMultiplier;
       const x = animationState.x + animationState.width / 2 + particle.x - pieceSize / 2;
       const y = animationState.y + animationState.height / 2 + particle.y - pieceSize / 2;
       const radius = Math.max(2, pieceSize * 0.2);
       
       ctx.beginPath();
       ctx.moveTo(x + radius, y);
       ctx.lineTo(x + pieceSize - radius, y);
       ctx.quadraticCurveTo(x + pieceSize, y, x + pieceSize, y + radius);
       ctx.lineTo(x + pieceSize, y + pieceSize - radius);
       ctx.quadraticCurveTo(x + pieceSize, y + pieceSize, x + pieceSize - radius, y + pieceSize);
       ctx.lineTo(x + radius, y + pieceSize);
       ctx.quadraticCurveTo(x, y + pieceSize, x, y + pieceSize - radius);
       ctx.lineTo(x, y + radius);
       ctx.quadraticCurveTo(x, y, x + radius, y);
       ctx.closePath();
       ctx.fill();
       
       ctx.restore();
    });
    
    // 애니메이션이 끝나면 제거
    if (progress >= 1) {
      return true; // 애니메이션 완료
    }
    return false; // 애니메이션 계속
  }, []);

  // 게임 시작/종료 관리
  useEffect(() => {
    if (showResult) return;
    let stopped = false;
    
    const startGame = async () => {
      hitNotesRef.current.clear();
      missedNotesRef.current.clear();
      perfectGroupsRef.current = 0;
      lastFrameTimeRef.current = 0;
      smoothTimeRef.current = 0;
      frameCountRef.current = 0;
      popAnimationsRef.current.clear(); // 애니메이션 상태 초기화
      gameLoop();
    };
    startGame();

    async function gameLoop(currentTime = 0) {
      if (stopped || showResult) return;
      
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) {
        animationRef.current = requestAnimationFrame((time) => gameLoop(time));
        return;
      }

      // 부드러운 시간 계산을 위한 프레임 간격 처리
      if (lastFrameTimeRef.current === 0) {
        lastFrameTimeRef.current = currentTime;
      }
      
      const deltaTime = currentTime - lastFrameTimeRef.current;
      lastFrameTimeRef.current = currentTime;
      
      // 프레임 카운트 증가
      frameCountRef.current++;
      
      const canvasW = canvasWidth;
      const canvasH = canvasHeight;
      
      // 오디오 시간을 더 정확하게 가져오기
      let gameTime = 0;
      try {
        gameTime = currentTimeRef.current() * 1000;
        // 시간이 유효하지 않으면 이전 프레임 시간 사용
        if (gameTime <= 0 || isNaN(gameTime)) {
          gameTime = smoothTimeRef.current;
        } else {
          // 부드러운 시간 보간 (프레임 간 급격한 변화 방지) - 보간 정도를 줄임
          const smoothingFactor = 0.3; // 0.8에서 0.3으로 변경하여 더 정확하게
          smoothTimeRef.current = smoothTimeRef.current * smoothingFactor + gameTime * (1 - smoothingFactor);
          gameTime = smoothTimeRef.current;
        }
      } catch (e) {
        gameTime = smoothTimeRef.current;
      }
      
      const notes = currentBeatmap;

      // 화면 크기에 따른 판정선 위치 조정
      let hitZoneY;
      if (window.innerWidth <= 768) {
        const screenHeight = window.innerHeight;
        if (screenHeight < 600) {
          // 아이폰 SE 등 작은 화면용 - 더 큰 판정영역
          hitZoneY = canvasH * (1 / 2); // 하단에서 1/2 지점
        } else {
          // 일반 모바일용
          hitZoneY = canvasH * (3 / 5); // 하단에서 2/5 지점
        }
      } else {
        // 데스크톱용
        hitZoneY = canvasH * (3 / 5); // 하단에서 2/5 지점
      }
      const pxPerMs = hitZoneY / NOTE_TRAVEL_TIME;

      // 캔버스 초기화 + 배경
      ctx.clearRect(0, 0, canvasW, canvasH);
      ctx.fillStyle = 'rgba(0,0,0,0.8)';
      ctx.fillRect(0, 0, canvasW, canvasH);

      // 판정영역 배경 시각화 (판정선부터 아래 전체)
      ctx.save();
      const gradient = ctx.createLinearGradient(0, hitZoneY, 0, canvasH);
      gradient.addColorStop(0, 'rgb(75, 75, 75)');
      gradient.addColorStop(1, 'rgb(49, 49, 49)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, hitZoneY, canvasW, canvasH - hitZoneY);

      // 내부 하이라이트 효과
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.fillRect(0, hitZoneY + 5, canvasW, 10);
      ctx.globalAlpha = 1;
      ctx.restore();

      // 레인 구분선
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.66)';
      ctx.lineWidth = 2;
      for (let i = 1; i < 4; i++) {
        const x = (canvasW / 4) * i;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvasH);
        ctx.stroke();
      }

      // 판정선 강조 (시각 효과만)
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(0, hitZoneY);
      ctx.lineTo(canvasW, hitZoneY);
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#fff';
      ctx.stroke();
      ctx.closePath();
      ctx.restore();

      const laneWidth = canvasW / 4;

      // 노트 처리 및 렌더링 (부드러운 애니메이션 적용)
      notes.forEach(note => {
        const noteId = `${note.time}-${note.lane}`;
        const isHit = hitNotesRef.current.has(noteId);
        const isMissed = missedNotesRef.current.has(noteId);
        
        // 이미 처리된 노트는 건너뛰기
        if (isHit || isMissed) return;

        const appearTime = note.time - NOTE_TRAVEL_TIME;
        
        // 부드러운 노트 위치 계산
        let noteTop;
        if (gameTime < appearTime) {
          // 노트가 아직 나타나지 않음
          noteTop = -50; // 화면 밖
        } else if (gameTime <= note.time) {
          // 노트가 판정선으로 이동 중 (일정한 속도)
          const progress = Math.max(0, Math.min(1, (gameTime - appearTime) / NOTE_TRAVEL_TIME));
          noteTop = progress * hitZoneY;
        } else {
          // 노트가 판정선을 지나감
          const overTime = gameTime - note.time;
          noteTop = hitZoneY + overTime * pxPerMs;
        }

        // 노트 미처리 시 미스 처리
        if (gameTime > note.time + MISS_WINDOW) {
          evaluateGroup(note.time, [note]);
        }

        const x = laneWidth * note.lane + laneWidth * 0.1;
        const width = laneWidth * 0.8;
        const height = 20 * (canvasH / canvasHeight);
        
        // 노트가 화면에 보이는 경우에만 렌더링
        if (noteTop + height >= -10 && noteTop <= canvasH + 10) {
          ctx.save();
          
          // 부드러운 그림자 효과
          ctx.shadowColor = '#f76319';
          ctx.shadowBlur = 15;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 2;
          
          // 노트 그라데이션 효과 (CSS 변수 색상 사용)
          const noteGradient = ctx.createLinearGradient(x, noteTop, x, noteTop + height);
          noteGradient.addColorStop(0, '#f76319'); // primary-color
          noteGradient.addColorStop(0.5, '#ff8548'); // secondary-color
          noteGradient.addColorStop(1, '#e55a15'); // primary-color 어두운 버전
          
          ctx.beginPath();
          ctx.moveTo(x + 10, noteTop);
          ctx.lineTo(x + width - 10, noteTop);
          ctx.quadraticCurveTo(x + width, noteTop, x + width, noteTop + 10);
          ctx.lineTo(x + width, noteTop + height - 10);
          ctx.quadraticCurveTo(x + width, noteTop + height, x + width - 10, noteTop + height);
          ctx.lineTo(x + 10, noteTop + height);
          ctx.quadraticCurveTo(x, noteTop + height, x, noteTop + height - 10);
          ctx.lineTo(x, noteTop + 10);
          ctx.quadraticCurveTo(x, noteTop, x + 10, noteTop);
          ctx.closePath();
          
          ctx.fillStyle = noteGradient;
          ctx.fill();
          
          // 테두리
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#ffceb5'; // scrollbar-color
          ctx.stroke();
          
          // 하이라이트 효과
          ctx.globalAlpha = 0.3;
          ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
          ctx.fillRect(x + 2, noteTop + 2, width - 4, height / 3);
          ctx.globalAlpha = 1;
          
          ctx.restore();
        }
      });

      // 풍선 터지는 효과 렌더링
      const completedAnimations = [];
      popAnimationsRef.current.forEach((animationState, noteId) => {
        const isCompleted = renderPopAnimation(ctx, animationState, performance.now());
        if (isCompleted) {
          completedAnimations.push(noteId);
        }
      });
      
      // 완료된 애니메이션 제거
      completedAnimations.forEach(noteId => {
        popAnimationsRef.current.delete(noteId);
      });

      // 그룹 노트 처리
      Object.entries(groupedNotesRef.current).forEach(([groupId, group]) => {
        const key = Number(groupId);
        const allProcessed = group.every(note => hitNotesRef.current.has(`${note.time}-${note.lane}`) || missedNotesRef.current.has(`${note.time}-${note.lane}`));
        if (allProcessed) return;

        const allMissed = group.every(note =>
          !hitNotesRef.current.has(`${note.time}-${note.lane}`) &&
          !missedNotesRef.current.has(`${note.time}-${note.lane}`) &&
          gameTime > note.time + MISS_WINDOW
        );
                 if (allMissed) {
           evaluateGroup(key, group);
         }
      });

      // 레인 클릭 효과 강조 (멀티터치 지원)
      if (activeLanes.size > 0) {
        activeLanes.forEach(laneIndex => {
          const x = laneWidth * laneIndex;
          ctx.save();
          ctx.fillStyle = 'rgba(255,255,255,0.12)';
          ctx.fillRect(x, 0, laneWidth, canvasHeight);
          ctx.restore();
        });
      }

      // 게임 종료(음악 끝 or 마지막 노트 + 여유시간)
      const lastNote = currentBeatmap[currentBeatmap.length - 1];
      const gameEndTime = lastNote.time + rhythmConfig.GAME_END_BUFFER;

      if (gameTime >= gameEndTime) {
        if (!showResult) {
          onGameEnd({
            score: scoreRef.current,
            combo: maxComboRef.current,
            timestamp: toKstIso(Timestamp.now())
          });
          setShowResult(true);
          audioController.stop();
        }
        return;
      }
      
      // 다음 프레임 요청 (부드러운 60fps 유지)
      animationRef.current = requestAnimationFrame((time) => gameLoop(time));
    }
    
    return () => {
      stopped = true;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
     }, [showResult, canvasWidth, canvasHeight, currentTimeRef, renderPopAnimation, evaluateGroup]);

  return {
    // 렌더링 관련 함수들
    startPopAnimation
  };
};
